import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api, fileUrl } from "@/lib/api";
import { ImageUploader } from "@/components/admin/ImageUploader";

const input = "w-full bg-background border border-border px-3 py-2 text-sm outline-none focus:border-foreground transition-colors";
const labelCls = "text-[11px] tracking-[0.12em] uppercase text-muted-foreground block mb-1.5";
const EMPTY = { title: "", short_description: "", description: "", image: "", order: 0 };

export default function AdminServices() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/services").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    if (!f.title) { toast.error("Title is required"); return; }
    setSaving(true);
    try {
      const payload = { ...f, order: Number(f.order) || 0 };
      delete payload.id; delete payload.slug; delete payload.created_at;
      if (editing === "new") await api.post("/services", payload);
      else await api.put(`/services/${editing}`, payload);
      toast.success("Saved"); setEditing(null); load();
    } catch { toast.error("Failed to save"); } finally { setSaving(false); }
  };
  const remove = async (id) => { if (!window.confirm("Delete this service?")) return; await api.delete(`/services/${id}`); toast.success("Deleted"); load(); };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div><h1 className="font-display font-extrabold text-3xl tracking-tight">Services</h1><p className="text-muted-foreground text-sm mt-1">{items.length} services</p></div>
        <button onClick={() => { setF(EMPTY); setEditing("new"); }} data-testid="new-service-btn" className="flex items-center gap-2 bg-foreground text-background px-5 py-2.5 text-[12px] tracking-[0.1em] uppercase hover:bg-onyx-wood hover:text-onyx-black transition-colors"><Plus size={15} /> New Service</button>
      </div>

      <div className="border border-border bg-card divide-y divide-border">
        {items.map((s) => (
          <div key={s.id} className="flex items-center gap-4 px-5 py-4" data-testid={`service-item-${s.id}`}>
            <div className="w-16 h-12 bg-secondary overflow-hidden shrink-0">{s.image && <img src={fileUrl(s.image)} alt="" className="w-full h-full object-cover" />}</div>
            <div className="flex-1 min-w-0"><div className="font-medium">{s.title}</div><div className="text-xs text-muted-foreground truncate">{s.short_description}</div></div>
            <button onClick={() => { setF({ ...EMPTY, ...s }); setEditing(s.id); }} data-testid={`edit-service-${s.id}`} className="p-2 text-muted-foreground hover:text-foreground"><Pencil size={16} /></button>
            <button onClick={() => remove(s.id)} className="p-2 text-muted-foreground hover:text-destructive"><Trash2 size={16} /></button>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/50 flex justify-end" onClick={() => setEditing(null)}>
          <div className="w-full max-w-xl bg-background h-full overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="service-editor">
            <div className="sticky top-0 bg-card border-b border-border px-6 h-16 flex items-center justify-between">
              <h2 className="font-display font-bold text-lg">{editing === "new" ? "New Service" : "Edit Service"}</h2><button onClick={() => setEditing(null)}><X /></button>
            </div>
            <div className="p-6 space-y-4">
              <div><label className={labelCls}>Title</label><input data-testid="service-title" className={input} value={f.title} onChange={(e) => set("title", e.target.value)} /></div>
              <div><label className={labelCls}>Short Description</label><input className={input} value={f.short_description} onChange={(e) => set("short_description", e.target.value)} /></div>
              <div><label className={labelCls}>Full Description</label><textarea rows={5} className={`${input} resize-none`} value={f.description} onChange={(e) => set("description", e.target.value)} /></div>
              <div><label className={labelCls}>Order</label><input type="number" className={input} value={f.order} onChange={(e) => set("order", e.target.value)} /></div>
              <ImageUploader value={f.image} onChange={(v) => set("image", v)} label="Image" testid="service-image-upload" />
              <button onClick={save} disabled={saving} data-testid="save-service" className="w-full bg-foreground text-background py-3 text-[12px] tracking-[0.1em] uppercase hover:bg-onyx-wood hover:text-onyx-black transition-colors disabled:opacity-50">{saving ? "Saving…" : "Save Service"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
